// Tiny typed wrapper around Meta Pixel (`window.fbq`) and GA4
// (`window.gtag`). Call sites import these helpers instead of touching
// the globals directly, so:
//   • the globals stay typed (see the `Window` augmentation below);
//   • a missing pixel ID / GA4 ID is a silent no-op at runtime — the
//     loader snippets in `index.html` simply don't install `fbq` /
//     `gtag` when their env vars are absent, and these helpers
//     early-return when the global isn't there;
//   • we never duplicate the camelCase / snake_case naming choice at
//     each call site — use `trackEvent({...}, {...})` to fire both at
//     once with their target-appropriate names.

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

type PixelOptions = {
  // `true` → `fbq('track', name, params)` — for Meta's known standard
  // events (PageView, CompleteRegistration, etc) so their ML
  // optimisation recognises them.
  // `false`/omitted → `fbq('trackCustom', name, params)` for custom
  // events.
  standard?: boolean;
};

export function trackPixel(
  name: string,
  params?: Record<string, unknown>,
  opts: PixelOptions = {},
): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  const verb = opts.standard ? 'track' : 'trackCustom';
  if (params) {
    window.fbq(verb, name, params);
  } else {
    window.fbq(verb, name);
  }
}

export function trackGa(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  if (params) {
    window.gtag('event', name, params);
  } else {
    window.gtag('event', name);
  }
}

// Convenience: fire to both Meta Pixel and GA4 in one call. Each
// platform has different naming conventions (camelCase for Pixel
// custom events, snake_case for GA4 recommended events), so each
// side carries its own name. Use this at every event call site so
// "also send to GA4" is impossible to forget.
export function trackEvent(
  meta: {
    name: string;
    standard?: boolean;
    params?: Record<string, unknown>;
  },
  ga: {
    name: string;
    params?: Record<string, unknown>;
  },
): void {
  trackPixel(meta.name, meta.params, { standard: meta.standard });
  trackGa(ga.name, ga.params);
}

// Store-badge click. Fires from every App Store / Google Play badge
// on the site (UnderConstruction landing, Hero, FloatingCTARail mobile
// bottom panel, and the RegisterModal success step). One helper keeps
// the `store` value canonical — Meta + GA4 both receive
// { store: 'apple' | 'google' }.
export function trackStoreClick(store: 'apple' | 'google'): void {
  trackEvent(
    { name: 'ClickPreRegisterStore', params: { store } },
    { name: 'click_pre_register_store', params: { store } },
  );
}
